import React, { useState, useMemo, useEffect, useRef } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import { ReportTableWrapper } from '@/components/reports/ReportTableWrapper';
import { useDivisionRebateSummary } from '@/api/rebates/useReports';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import type { ReportLineType, ReportTimeFilter } from '@erp/shared-types';
import {
  LINE_TYPE_LABELS,
  LINE_TYPE_DESCRIPTIONS,
} from '@/types/rebateAnalytics';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ShareNetworkIcon,
  LinkIcon,
  XIcon,
  DownloadIcon,
} from '@phosphor-icons/react';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import { DownloadModal } from 'src/components/reports/DownloadModal';
import { downloadMultiTableCSV } from '@/utils/csvDownload';
import { getVisibleColumnIndices } from '@/utils/reportTableUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const REPORT_ID = 'division-rebate-summary';
const REPORT_TITLE = 'Division Rebate Summary';

const DivisionRebateSummaryPage: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [lineType, setLineType] = useState<ReportLineType>('reported');
  // Default time filter based on year: 'current' for current year, 'all' for past years
  const [ruleTagTimeFilter, setRuleTagTimeFilter] = useState<ReportTimeFilter>(
    currentYear === new Date().getFullYear() ? 'current' : 'all',
  );
  const [supplierTimeFilter, setSupplierTimeFilter] =
    useState<ReportTimeFilter>(
      currentYear === new Date().getFullYear() ? 'current' : 'all',
    );
  const hasSetDefaultDivision = useRef(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  // Generate default filename with divisions
  const defaultFilename = useMemo(() => {
    const divisionPart =
      selectedDivisions.length > 0
        ? `-${selectedDivisions.map((d) => d.label.replace(/\s+/g, '-')).join('-')}`
        : '';
    return `division-rebate-summary-${selectedYear}${divisionPart}`;
  }, [selectedYear, selectedDivisions]);

  // Handler for downloading all tables
  const handleDownloadClick = () => {
    if (!report) return;
    setDownloadModalOpen(true);
  };

  const handleDownload = (filename: string) => {
    if (!report) return;

    // Compute visible columns based on time filters
    const ruleTagVisibleColumns = getVisibleColumnIndices(
      report.ruleTagTable,
      ruleTagTimeFilter,
      selectedYear,
    );
    const supplierVisibleColumns = getVisibleColumnIndices(
      report.supplierTable,
      supplierTimeFilter,
      selectedYear,
    );

    downloadMultiTableCSV({
      tables: [
        {
          title: report.ruleTagTable.title || 'Rebates by Rule Tag',
          table: report.ruleTagTable,
          rowLabelHeader: 'Category',
          visibleColumnIndices: ruleTagVisibleColumns,
        },
        {
          title: report.supplierTable.title || 'Rebates by Supplier',
          table: report.supplierTable,
          rowLabelHeader: 'Supplier',
          visibleColumnIndices: supplierVisibleColumns,
        },
      ],
      filename,
    });
  };

  // Fetch divisions for filter
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Prepare division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare year options - descending order, no future years
  const yearOptions = useMemo(() => {
    if (!availableYears || availableYears.length === 0) {
      // Fallback to last 5 years if no years available
      const years: number[] = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years.map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
    }
    return availableYears
      .filter((year) => year <= currentYear) // Exclude future years
      .sort((a, b) => b - a) // Descending order
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears, currentYear]);

  // Default to first division option when divisions are loaded (only once)
  useEffect(() => {
    if (
      divisionOptions.length > 0 &&
      selectedDivisions.length === 0 &&
      !divisionsLoading &&
      !hasSetDefaultDivision.current
    ) {
      setSelectedDivisions([divisionOptions[0]]);
      hasSetDefaultDivision.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionOptions.length, divisionsLoading]);

  // Update time filters when year changes
  useEffect(() => {
    const defaultTimeFilter: ReportTimeFilter =
      selectedYear < currentYear ? 'all' : 'current';
    setRuleTagTimeFilter(defaultTimeFilter);
    setSupplierTimeFilter(defaultTimeFilter);
  }, [selectedYear, currentYear]);

  // Prepare line type options
  const lineTypeOptions = useMemo(() => {
    return (['estimates', 'predictions', 'reported'] as const).map((type) => ({
      id: type,
      label: LINE_TYPE_LABELS[type] || type,
      value: type,
    }));
  }, []);

  // Fetch report data
  const {
    data: report,
    isLoading,
    error,
  } = useDivisionRebateSummary({
    year: selectedYear,
    divisionIds:
      selectedDivisions.length > 0
        ? selectedDivisions.map((d) => d.id)
        : undefined,
    lineType,
  });

  return (
    <DashboardLayout>
      <div className="p-6 w-full min-w-0 max-w-full overflow-hidden">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">
              Division Rebate Summary - {selectedYear}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              View rebate summaries aggregated by rule tags and suppliers
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadClick}
              disabled={!report || isLoading}
            >
              <DownloadIcon className="h-4 w-4 mr-2" />
              Download All
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <ShareNetworkIcon className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                  <ShareNetworkIcon className="h-4 w-4 mr-2" />
                  Create share link
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLinksManagerOpen(true)}>
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Manage shared links
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Filters Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-6">
              <SingleSelect
                label="Year"
                labelPosition="left"
                options={yearOptions}
                value={
                  yearOptions.find((opt) => opt.value === selectedYear) || null
                }
                onChange={(option) => {
                  if (option) {
                    setSelectedYear(option.value);
                  }
                }}
                placeholder="Select year..."
                searchPlaceholder="Search years..."
                shouldAutoSort={false}
              />
              <MultiSelect
                label="Divisions"
                labelPosition="left"
                options={divisionOptions}
                value={selectedDivisions}
                onChange={setSelectedDivisions}
                placeholder="Select divisions..."
                searchPlaceholder="Search divisions..."
                disabled={divisionsLoading}
              />
              <div>
                <SingleSelect
                  label="Line Type"
                  labelPosition="left"
                  options={lineTypeOptions}
                  value={
                    lineTypeOptions.find((opt) => opt.id === lineType) || null
                  }
                  onChange={(option) => {
                    if (option) {
                      setLineType(option.id as ReportLineType);
                    }
                  }}
                  placeholder="Select line type..."
                  searchPlaceholder="Search line types..."
                />
                {lineType && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {LINE_TYPE_DESCRIPTIONS[lineType]}
                  </p>
                )}
              </div>
            </div>

            {/* Selected Divisions Pills */}
            {selectedDivisions.length > 0 && (
              <div className="pt-3 border-t">
                <p className="text-sm text-muted-foreground mb-2">
                  Selected Divisions ({selectedDivisions.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedDivisions.length === divisionOptions.length ? (
                    <Badge variant="secondary" className="gap-1 pl-3 pr-2 py-1">
                      <span>All Divisions</span>
                    </Badge>
                  ) : (
                    selectedDivisions.map((division) => (
                      <Badge
                        key={division.id}
                        variant="secondary"
                        className="gap-1 pl-3 pr-1 py-1"
                      >
                        <span>{division.label}</span>
                        <button
                          onClick={() =>
                            setSelectedDivisions(
                              selectedDivisions.filter(
                                (d) => d.id !== division.id,
                              ),
                            )
                          }
                          className="ml-1 hover:bg-muted rounded-full p-0.5"
                          title={`Remove ${division.label}`}
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Rebates by Rule Tag</CardTitle>
              </CardHeader>
              <CardContent>
                <TableSkeleton />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Rebates by Supplier</CardTitle>
              </CardHeader>
              <CardContent>
                <TableSkeleton />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-destructive">
                Error loading report data. Please try again.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Report Tables */}
        {report && !isLoading && (
          <div className="space-y-6">
            <ReportTableWrapper
              table={report.ruleTagTable}
              timeFilter={ruleTagTimeFilter}
              onTimeFilterChange={setRuleTagTimeFilter}
              downloadFilename={`rebates-by-rule-tag-${selectedYear}`}
              showTotals={true}
              year={selectedYear}
            />
            <ReportTableWrapper
              table={report.supplierTable}
              timeFilter={supplierTimeFilter}
              onTimeFilterChange={setSupplierTimeFilter}
              downloadFilename={`rebates-by-supplier-${selectedYear}`}
              showTotals={true}
              year={selectedYear}
            />
          </div>
        )}
      </div>

      <ShareReportModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
      />
      <SharedLinksManager
        open={linksManagerOpen}
        onClose={() => setLinksManagerOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
        onCreateNew={() => {
          setLinksManagerOpen(false);
          setShareModalOpen(true);
        }}
      />
      <DownloadModal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        onDownload={handleDownload}
        defaultFilename={defaultFilename}
        title="Download Division Rebate Summary"
      />
    </DashboardLayout>
  );
};

export default DivisionRebateSummaryPage;
